/**
 * Provider-agnostic hook that returns the authenticated user's ID.
 *
 * Reads the application's `authProvider` config and pulls the user ID
 * from the matching auth context (Kinde or Clerk). Uses raw
 * `React.useContext` on each provider's context so it returns
 * `undefined` instead of throwing when the provider isn't mounted.
 */
import { KindeContext } from "@kinde-oss/kinde-auth-react";
import { UserContext } from "@clerk/shared/react";
import { useContext } from "react";
import { useApplicationContext } from "@src/funnel/useApplicationContext";

export const useAuthUserId = (): string | undefined => {
  const ctx = useApplicationContext();
  const kindeCtx = useContext(KindeContext);
  // Clerk's createContextAndHook wraps the value as { value: T }.
  // Raw useContext returns undefined when ClerkProvider is absent.
  const clerkUserWrapper = useContext(UserContext) as
    | { value: { id: string } | null | undefined }
    | undefined;

  if (ctx.status !== "ready") return undefined;

  if (ctx.application.authProvider === "clerk") {
    return clerkUserWrapper?.value?.id;
  }

  // Default: Kinde
  return kindeCtx?.user?.id ?? undefined;
};
